
import { useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from 'react-redux';
import { Button, Form, Modal } from 'react-bootstrap';
import { FaFilter, FaPen, FaPlus, FaSearch, FaSyncAlt, FaTrash } from 'react-icons/fa';
import { toast } from "react-toastify";
import Swal from 'sweetalert2';
import { API_URL_All } from '../api';
import * as general from '../libs/general';
import Paging from '../components/Paging';
import { CheckingTicket, LockingTicket, WarningIcon, EyeIcon } from '../components/tickets/Tickets';
import { RankDiscountHover } from '../components/orders/RankDiscountInfo';
import OrderAdminFilter from '../components/filters/OrderAdminFilter';
import OrderEditor from '../components/orders/OrderEditor';
import MultiOrderEditor from '../components/orders/MultiOrderEditor';
import TransferCustomerEditor from '../components/orders/TransferCustomerEditor';
import TransferSackCode from '../components/orders/TransferSackCode';
import OrderVatEditor from '../components/orders/OrderVatEditor';
import HistoryInfo from '../components/orders/HistoryInfo';
import OrderCommentModal from '../components/orders/OrderCommentModal';
import AddOrderByExcelModal from '../components/orders/AddOrderByExcelModal';
import RecalculateFeeEditor from '../components/orders/RecalculateFeeEditor';
import AdLayout from '../components/AdLayout';
import DataTable from '../components/ui/DataTable';
import axiosInstance from '../axiosInstance';
import { formatOrderDateRangeLabel } from '../libs/lib-date-range-filter';
import { buildOrderAdminSearchParams, nextExpressFilterValue } from '../libs/orderAdminSearchParams';
import {
  canFilterMuaHoOrders,
  isMuaHoOnlyOrderViewer,
  MUA_HO_ORDER_FILTER_SUPPORT_VALUE,
  orderPermSliceForLine,
  resolveOrderMuaHoSupplierSectionAccess,
  fetchOrdersDivisionPermissions,
  fetchNotificationDivisionPermissions,
  ORDER_EXCEL_EXPORT_FEATURES,
  ORDER_QUICK_UPDATE_CHECKING_FEATURE,
  ORDER_QUICK_UPDATE_CONTACTING_FEATURE,
  ORDER_QUICK_UPDATE_FEATURES,
  ORDER_QUICK_UPDATE_LOCKING_FEATURE,
  ORDER_QUICK_UPDATE_PROGRESS_FEATURE,
  ORDER_QUICK_UPDATE_RECALCULATE_FEE_FEATURE,
  ORDER_QUICK_UPDATE_SACK_CODE_FEATURE,
  resolveOrderPermissionHiddenColumnKeys,
} from "../libs/customerCreatePermission";
import {
  ORDER_MANAGEMENT_COLUMNS,
  ORDER_MANAGEMENT_SCREEN_KEY,
  ORDER_LEGACY_DELETE_COLUMN_KEY,
  combinedScreenColumnClass,
  getToggleableScreenColumns,
} from "../libs/screenColumnPreference";
import { useScreenColumnPreferences } from "../libs/useScreenColumnPreferences";
import TableColumnSettingsButton from "../components/TableColumnSettingsButton";
import TableColumnSettingsModal from "../components/TableColumnSettingsModal";

const ORDER_KEYWORD_DEBOUNCE_MS = 400;

function isCanceledRequest(error) {
  return axios.isCancel(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
}

/** Khớp quyền phân bổ với `formType` trong modal / API cập nhật nhanh. */
function quickUpdateFeatureKeyForFormType(formType) {
  switch (formType) {
    case "progress":
      return ORDER_QUICK_UPDATE_PROGRESS_FEATURE;
    case "checking":
      return ORDER_QUICK_UPDATE_CHECKING_FEATURE;
    case "contacting":
      return ORDER_QUICK_UPDATE_CONTACTING_FEATURE;
    case "locking":
      return ORDER_QUICK_UPDATE_LOCKING_FEATURE;
    default:
      return null;
  }
}

/** Khớp backend CommonConstant.MD_LINE_VAT (12); dùng Number vì lineId từ JSON đôi khi là chuỗi. */
const ORDER_VAT_LINE_ID = 12;
const isOrderVatLine = (order) => Number(order?.lineId) === ORDER_VAT_LINE_ID;

function OrderAdmin() {
  const navigate = useNavigate();
  const { hiddenCols, setHiddenCols, loaded: columnSettingsLoaded } = useScreenColumnPreferences(
    ORDER_MANAGEMENT_SCREEN_KEY,
    ORDER_MANAGEMENT_COLUMNS,
    { legacyLocalStorageKey: "table_setting" }
  );
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [permissionHiddenCols, setPermissionHiddenCols] = useState([]);
  const [pageData, setPageData] = useState({ content: [] });
  const [errorMessage, setErrorMessage] = useState("");
  const [orderScreenPerm, setOrderScreenPerm] = useState({
    loaded: false,
    menuView: false,
    normal: { view: false, create: false, edit: false, delete: false },
    vat: { view: false, create: false, edit: false, delete: false },
    excel: {},
    quickUpdate: {},
    muaHo: false,
    canFilterMuaHo: false,
    muaHoOnlyViewer: false,
    muaHoNormal: { view: false, create: false, edit: false, delete: false },
    muaHoVat: { view: false, create: false, edit: false, delete: false },
  });

  const orderFeaturePerm = {
    loaded: orderScreenPerm.loaded,
    canView: orderScreenPerm.menuView,
    canCreate:
      orderScreenPerm.normal.create ||
      orderScreenPerm.vat.create ||
      orderScreenPerm.muaHoNormal.create ||
      orderScreenPerm.muaHoVat.create,
    canEdit:
      orderScreenPerm.normal.edit ||
      orderScreenPerm.vat.edit ||
      orderScreenPerm.muaHoNormal.edit ||
      orderScreenPerm.muaHoVat.edit,
    canDelete:
      orderScreenPerm.normal.delete ||
      orderScreenPerm.vat.delete ||
      orderScreenPerm.muaHoNormal.delete ||
      orderScreenPerm.muaHoVat.delete,
  };

  const rowOrderPerm = (order) => orderPermSliceForLine(orderScreenPerm, order?.lineId);

  const canCreateMuaHoNormal = orderScreenPerm.muaHoNormal.create;
  const canCreateMuaHoVat = orderScreenPerm.muaHoVat.create;
  const canCreateMuaHoAny = canCreateMuaHoNormal || canCreateMuaHoVat;

  const showMuaHoFilterButton =
    orderScreenPerm.loaded &&
    canFilterMuaHoOrders(orderScreenPerm) &&
    !isMuaHoOnlyOrderViewer(orderScreenPerm);

  const muaHoSupplierFlagsForOrder = (lineId, orderId, extra = {}) => {
    const access = resolveOrderMuaHoSupplierSectionAccess(orderScreenPerm, lineId, orderId);
    return {
      showOrderSupplierSection: access.show,
      muaHoSupplierReadOnly: !access.editable,
      requireMuaHoSupplier: Boolean(extra.requireMuaHoSupplier),
    };
  };

  const orderExcelPerm = (divisionFeature) => Boolean(orderScreenPerm.excel?.[divisionFeature]);
  const anyOrderExcelPerm = ORDER_EXCEL_EXPORT_FEATURES.some((f) => orderExcelPerm(f));
  const orderQuickUpdatePerm = (divisionFeature) => Boolean(orderScreenPerm.quickUpdate?.[divisionFeature]);
  /** Dropdown Cập nhật nhanh: cần quyền Xem «Cập nhật nhanh» và quyền Sửa đơn thường hoặc VAT. */
  const canUseOrderQuickUpdateToolbar =
    ORDER_QUICK_UPDATE_FEATURES.some((f) => orderQuickUpdatePerm(f)) &&
    (orderScreenPerm.normal.edit || orderScreenPerm.vat.edit);

  const [vatEditorData, setVatEditorData] = useState(null);
  const [orderEditorData, setOrderEditorData] = useState(null);
  const [multiOrderEditorData, setMultiOrderEditorData] = useState(null);
  const [transterCustomerData, setTransterCustomerData] = useState(null);
  const [notifyPerm, setNotifyPerm] = useState({
    loaded: false,
    transferRequestInbox: false,
    transferRequestProcess: false,
  });
  const transferOpenDeniedRef = useRef(false);
  const [transterSackCodeData, setTransterSackCodeData] = useState(null);
  const [recalculateFeeData, setRecalculateFeeData] = useState(null);

  const [filterData, setFilterData] = useState(null);
  const [historyInfoData, setHistoryInfoData] = useState(null);
  const [commentModalData, setCommentModalData] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showQuickUpdate, setShowQuickUpdate] = useState(false);

  const [expressFormData, setExpressFormData] = useState({});
  const [configData, setConfigData] = useState(() => general.getLocalStorage("user_config") || {});

  const refreshConfigData = () => {
    setConfigData(general.getLocalStorage("user_config") || {});
  };

  useEffect(() => {
    let cancelled = false;

    const ensureConfigData = async () => {
      let cfg = general.getLocalStorage("user_config") || {};
      const needsFetch = !cfg?.normalLineNames || !cfg?.lineNames || !cfg?.progressNames;
      if (needsFetch) {
        try {
          const res = await axiosInstance.get("/api/users/getConfig");
          if (cancelled) return;
          if (res?.data?.data) {
            general.setLocalStorage("user_config", res.data.data);
            cfg = res.data.data;
          }
        } catch (error) {
          console.error(error);
        }
      }
      if (!cancelled) setConfigData(cfg || {});
    };

    ensureConfigData();

    const onConfigLoaded = () => {
      if (!cancelled) refreshConfigData();
    };
    window.addEventListener("user-config-loaded", onConfigLoaded);

    return () => {
      cancelled = true;
      window.removeEventListener("user-config-loaded", onConfigLoaded);
    };
  }, []);

  const year = new Date().getFullYear();
  const hasRun = useRef(false);
  const searchSeqRef = useRef(0);
  const searchAbortRef = useRef(null);
  const keywordTimerRef = useRef(null);

  useEffect(() => () => {
    searchSeqRef.current += 1;
    searchAbortRef.current?.abort();
    if (keywordTimerRef.current) {
      clearTimeout(keywordTimerRef.current);
      keywordTimerRef.current = null;
    }
  }, []);

  const location = useLocation();
  const currentUser = useSelector((state) => state.user.user) || {};
  const normalizeFeatureName = (value) =>
    String(value || "")
      .normalize("NFC")
      .replace(/\s+/g, " ")
      .replace(/\s*\/\s*/g, " / ")
      .trim()
      .toLowerCase();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const n = await fetchNotificationDivisionPermissions(axiosInstance);
      if (cancelled) return;
      setNotifyPerm({
        loaded: true,
        transferRequestInbox: Boolean(n.transferRequestInbox),
        transferRequestProcess: Boolean(n.transferRequestProcess),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await fetchOrdersDivisionPermissions(axiosInstance);
      if (cancelled) return;
      setOrderScreenPerm({
        loaded: true,
        menuView: Boolean(p?.menuView),
        normal: p.normal,
        vat: p.vat,
        excel: p.excel || {},
        quickUpdate: p.quickUpdate || {},
        muaHo: Boolean(p?.muaHo),
        canFilterMuaHo: Boolean(p?.canFilterMuaHo ?? p?.muaHo),
        muaHoOnlyViewer: Boolean(p?.muaHoOnlyViewer),
        muaHoNormal: p.muaHoNormal || { view: false, create: false, edit: false, delete: false },
        muaHoVat: p.muaHoVat || { view: false, create: false, edit: false, delete: false },
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const loadColumnPermissions = async () => {
      try {
        const flagsRes = await axiosInstance.get("/api/users/myFlags");
        const flags = flagsRes?.data?.data || {};
        if (flags?.isAdmin === true) {
          setPermissionHiddenCols([]);
          return;
        }
        if (flags?.isMember === true) {
          const permissionRes = await axiosInstance.get("/api/users/myDivisionPermissions");
          const permissionRows = permissionRes?.data?.data || [];
          setPermissionHiddenCols(resolveOrderPermissionHiddenColumnKeys(permissionRows));
          return;
        }
      } catch (error) {
      }
      setPermissionHiddenCols([]);
    };
    loadColumnPermissions();
  }, [currentUser?.email]);

  const [params, setParams] = useState({});
  var selectedIds = [];
  var selectedDetailIds = [];
  const [countCountOrderByStatusList, setCountOrderByStatusList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoading2, setIsLoading2] = useState(false);
  const [operationObj, setOperationObj] = useState({});
  const [selectedOrderDetails, setSelectedOrderDetails] = useState([]);
  const ORDER_PROGRESS = ['Đơn mới', 'Kho TQ', 'Xe về VN', 'Kho VN', 'Đang giao', 'Đã nhận'];
  const ORDER_PROGRESS_CONFIG_KEY = 'order_progress_warning_config';
  const getDefaultOrderProgressConfig = () =>
    Object.fromEntries(ORDER_PROGRESS.map((name) => [name, true]));
  const getOrderProgressConfigFromStorage = () => {
    if (typeof window === 'undefined') return getDefaultOrderProgressConfig();
    try {
      const text = localStorage.getItem(ORDER_PROGRESS_CONFIG_KEY);
      if (!text) return getDefaultOrderProgressConfig();

      const parsed = JSON.parse(text);
      if (!parsed || typeof parsed !== 'object') return getDefaultOrderProgressConfig();

      return ORDER_PROGRESS.reduce((acc, name) => {
        acc[name] = typeof parsed[name] === 'boolean' ? parsed[name] : true;
        return acc;
      }, {});
    } catch {
      return getDefaultOrderProgressConfig();
    }
  };
  const [addOrderByExelModal, setAddOrderByExelModal] = useState(false);

  useEffect(() => {
    const pathMatch = location.pathname.match(/overdueId=(\d+)/i);
    const queryOverdue = new URLSearchParams(location.search).get('overdueId');
    if (pathMatch && !queryOverdue) {
      navigate(`/quan-li-don-hang?overdueId=${pathMatch[1]}`, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!orderFeaturePerm.loaded || !orderFeaturePerm.canView) return;

    let cancelled = false;
    const overdueId = getParamFromUrl('overdueId');

    (async () => {
      if (!general.isNumber(overdueId)) {
        // Mặc định filter khi vào từ menu "Đơn Thường"/"Đơn VAT" (2 cấp) — chỉ set giá trị khởi
        // tạo cho filter "Line" đã có sẵn, KHÔNG khoá cứng: người dùng vẫn chỉnh được sau đó.
        const linesParam = getParamFromUrl('lines');
        if (linesParam) {
          searchOrders({ page: 0, lines: linesParam });
        } else {
          searchOrders();
        }
        return;
      }

      let cfg = general.getLocalStorage('user_config') || {};
      if (!cfg.progressList?.length) {
        try {
          const res = await axiosInstance.get('/api/users/getConfig');
          if (cancelled) return;
          if (res?.data?.data) {
            general.setLocalStorage('user_config', res.data.data);
            cfg = res.data.data;
            setConfigData(cfg);
          }
        } catch (error) {
          console.error(error);
        }
      }
      if (cancelled) return;

      const progressList = cfg?.progressList || [];
      const obj = progressList.find((u) => u.id === parseInt(overdueId, 10));
      const progressName = obj ? obj.name : '';
      requestSearchOrders({
        page: 0,
        status: progressName,
        overdue: 'Đơn chậm',
        support: '',
        draft: '',
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [orderFeaturePerm.loaded, orderFeaturePerm.canView, location.pathname, location.search]);

  useEffect(() => {
    if (!configData?.progressList?.length) {
      window.dispatchEvent(new Event("load-data-config"));
    }
  }, [orderFeaturePerm.loaded, orderFeaturePerm.canView, configData?.progressList?.length]);

    
  const handleSearchChange = (e) => {
    if (!orderFeaturePerm.canView) return;
    const searchValue = e.target.value;
    setSearchQuery(searchValue);
    if (keywordTimerRef.current) clearTimeout(keywordTimerRef.current);
    keywordTimerRef.current = setTimeout(() => {
      keywordTimerRef.current = null;
      searchOrders({ page: 0, keyword: searchValue });
    }, ORDER_KEYWORD_DEBOUNCE_MS);
  };

  const requestSearchOrders = async (filter) => {
    if (!orderFeaturePerm.canView) return;
    var checkAllTag = document.querySelector('.check_all');
    if (checkAllTag) {
      checkAllTag.removeAttribute('data-unchecked');
      checkAllTag.checked = false;
    }
    setSelectedOrders([]);
    setExpressFormData({});
    setSelectedOrderDetails([]);

    searchOrders(filter);
  }


  const searchOrders = async (filter) => {
    if (!orderFeaturePerm.canView) return;
    const overdueId = getParamFromUrl('overdueId');
    if (general.isNumber(overdueId) && !filter) return;

    if (keywordTimerRef.current) {
      clearTimeout(keywordTimerRef.current);
      keywordTimerRef.current = null;
    }

    const requesterId = getParamFromUrl('requesterId');
    const pendingUserId = getParamFromUrl('pendingUserId');
    const readBatchId = getParamFromUrl('readBatchId');
    if (!general.isNumber(overdueId) && !general.isNumber(requesterId) && !general.isNumber(pendingUserId) && !general.isNumber(readBatchId)) {
      general.clearUrlParam();
    }

    const seq = ++searchSeqRef.current;
    searchAbortRef.current?.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;
    setIsLoading(true);
    setErrorMessage('');

    const isFirstSearch = !hasRun.current;
    if (isFirstSearch) hasRun.current = true;

    try {
      const formData = general.getFormData('#form_filter');
      const params = buildOrderAdminSearchParams({
        session: general.getSession('search_orders'),
        formData,
        filter,
        isFirstSearch,
        orderScreenPerm,
      });
      if (isFirstSearch) setSearchQuery(params.keyword || '');

      general.setSession('search_orders', params);
      setParams(params);
      if (window.initializeFilterValue) window.initializeFilterValue(params);
      if (window.addFilterValue) {
        window.addFilterValue('dateRange', formatOrderDateRangeLabel(params.dateFrom, params.dateTo));
      }

      var filteredParams = Object.fromEntries(Object.entries(params).filter(([key, v]) => v !== undefined && v !== '' && key + v !== 'page0' && key + v !== 'size550' && key + v !== 'isDeletedfalse'));

      const response = await axiosInstance.get('/api/import-orders/searchorders', {
        params: filteredParams,
        signal: controller.signal,
      });
      if (seq !== searchSeqRef.current) return;
      setOperationObj(general.getLocalStorage("operation_info"));

      let processedOrders = response.data.page.content.map((order) => {
        return {
          ...order,
          tqCode: order.cnShippingCode ? order.cnShippingCode.substring(0, 3) : "",
          vnCode: order.vnShippingCode ? order.vnShippingCode.substring(0, 3) : "",
        };
      });

      const pagePayload = {
        ...response.data,
        page: {
          ...response.data.page,
          content: processedOrders,
        },
      };

      setPageData(pagePayload);
      setOrders(processedOrders);

      if (params.status) {
        var statusNames = String(params.status).split(';');
        var listStatus = response.data.orderStatus;
        listStatus.forEach(obj => {
          if (statusNames.includes(obj.name)) {
            obj.active = true;
          }
        });
      }
      setCountOrderByStatusList(response.data.orderStatus);

      var checkAllTag = document.querySelector('.check_all');

      if (checkAllTag) {
        var listIds = [];
        checkAllTag.removeAttribute('data-listIds');
        if (response.data.listIds) {
          listIds = response.data.listIds;
          checkAllTag.setAttribute('data-listIds', response.data.listIds.join(","));
        }
        var uncheckedList = checkAllTag.getAttribute('data-unchecked');
        if (uncheckedList || checkAllTag.checked) {
          if (!uncheckedList) uncheckedList = '';
          var uncheckedItems = uncheckedList.split(',').map(item => parseInt(item.trim())).filter(Number.isInteger);
          var selectedIds = listIds.filter(item => !uncheckedItems.includes(item));
          setSelectedOrders(selectedIds);
        }
      }
    } catch (error) {
      if (seq !== searchSeqRef.current || isCanceledRequest(error)) return;
      setPageData({ content: [] });
      setErrorMessage(error.message);
      console.error(error);
    } finally {
      if (seq === searchSeqRef.current) setIsLoading(false);
    }
  };
  useEffect(() => {
    checkOpenTransferCustomerEditor();
  }, [location, notifyPerm.loaded, notifyPerm.transferRequestInbox, notifyPerm.transferRequestProcess]);

  function getParamFromUrl(name) {
    const fromRouterSearch = new URLSearchParams(location.search).get(name);
    if (fromRouterSearch) return fromRouterSearch;

    const hash = window.location.hash || '';
    const hashQueryMatch = hash.match(/\?(.*)$/);
    if (hashQueryMatch) {
      const fromHashQuery = new URLSearchParams(hashQueryMatch[1]).get(name);
      if (fromHashQuery) return fromHashQuery;
    }

    if (name === 'overdueId' || name === 'requesterId' || name === 'pendingUserId' || name === 'readBatchId') {
      const pathMatch = location.pathname.match(new RegExp(`${name}=(\\d+)`, 'i'));
      if (pathMatch) return pathMatch[1];
      const hashPathMatch = hash.match(new RegExp(`${name}=(\\d+)`, 'i'));
      if (hashPathMatch) return hashPathMatch[1];
    }

    return null;
  }

  const checkOpenTransferCustomerEditor = () => {
    const requesterId = getParamFromUrl('requesterId');
    const pendingUserId = getParamFromUrl('pendingUserId');
    const readBatchId = getParamFromUrl('readBatchId');
    if (!general.isNumber(requesterId) && !general.isNumber(pendingUserId) && !general.isNumber(readBatchId)) {
      transferOpenDeniedRef.current = false;
      return;
    }
    if (!notifyPerm.loaded) return;
    if (!notifyPerm.transferRequestInbox) {
      if (!transferOpenDeniedRef.current) {
        transferOpenDeniedRef.current = true;
        toast.warning("Bạn không có quyền nhận yêu cầu chuyển đơn.");
      }
      return;
    }
    transferOpenDeniedRef.current = false;
    var obj = {};
    obj.listIds = [];
    if (general.isNumber(pendingUserId)) {
      obj.pendingUserId = parseInt(pendingUserId, 10);
    } else if (general.isNumber(readBatchId)) {
      obj.readBatchId = parseInt(readBatchId, 10);
    } else {
      obj.requesterId = parseInt(requesterId, 10);
    }
    setTransterCustomerData(obj);
  };

  const handleOpenModal = () => {
    //modalRef.current?.showModal(); // Gọi hàm từ component con
    setFilterData({});
  };

  const handleExpressFilter = async (name, value) => {
    if (name === "support") {
      if (orderScreenPerm.loaded && isMuaHoOnlyOrderViewer(orderScreenPerm)) {
        return;
      }
      if (orderScreenPerm.loaded && !canFilterMuaHoOrders(orderScreenPerm)) {
        return;
      }
    }
    const nextValue = nextExpressFilterValue(params?.[name], value);
    requestSearchOrders({ page: 0, [name]: nextValue });
  }

  const handleDeleteOrder = (order, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!order || order.id == null) {
      toast.warning("Không xác định được đơn hàng.");
      return;
    }
    const slice = rowOrderPerm(order);
    if (!slice.delete) {
      toast.warning("Bạn không có quyền xóa.");
      return;
    }
    general.openConfirm(async () => {
      try {
        var modelData = {};
        var requestApi = `/api/import-orders/deleteOrder/${order.id}`;
        const response = await axiosInstance.put(requestApi, modelData);

        if (!response.data.success) {
          toast.error(response.data.message || "Không thể xóa đơn hàng.");
          return;
        }
        toast.success(response.data.message || "Đã xóa.");
        requestSearchOrders({ page: 0 });
      } catch (error) {
        console.error(error);
        toast.error(error.message);
      }
    })
  };

  const handleDeleteOrder111 = async (id, e) => {
    activeCurrentRow(e);
    Swal.fire({
      title: "Bạn có chắc chắn?",
      text: "Sau khi xóa, bạn sẽ không thể khôi phục đơn hàng này!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Xóa ngay!",
      cancelButtonText: "Hủy"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await axiosInstance.put(`/api/import-orders/delete/${id}`);
          if (response.data === "Đơn hàng bị khóa nên không thể xóa!") {
            toast.error(response.data, { position: "top-right" });
            return;
          }

          setOrders(orders.filter((order) => order.id !== id));
          toast.success("Xóa đơn hàng thành công!", { position: "top-right" });
          Swal.fire("Đã xóa!", "Đơn hàng đã được xóa.", "success");
          searchOrders();
        } catch (error) {
          console.error("Lỗi khi xóa đơn hàng:", error);
          Swal.fire("Lỗi!", "Không thể xóa đơn hàng.", "error");
        }
      }
    });
  };

  const orderToggleableColumns = useMemo(
    () => getToggleableScreenColumns(ORDER_MANAGEMENT_SCREEN_KEY, ORDER_MANAGEMENT_COLUMNS, permissionHiddenCols),
    [permissionHiddenCols]
  );

  const getColCkeckedClass = (name) => combinedScreenColumnClass(hiddenCols, name, permissionHiddenCols);
  const isDeleteActionHidden =
    hiddenCols.includes(ORDER_LEGACY_DELETE_COLUMN_KEY) || hiddenCols.includes("actions");
  const handleChangeItemsPerPage = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    searchOrders({ page: 0, size: newItemsPerPage });
  };

  const handlePageChange = (page) => {
    searchOrders({ page: page - 1 });
  };


  const handleEditor = (id, lineId, e) => {
    const vatLine = Number(lineId) === ORDER_VAT_LINE_ID;
    const slice = orderPermSliceForLine(orderScreenPerm, lineId);
    if (!slice.view && !slice.edit) {
      toast.warning("Bạn không có quyền xem chi tiết.");
      return;
    }
    activeCurrentRow(e);
    const editorPayload = {
      id,
      lineId,
      readOnly: !slice.edit,
      ...muaHoSupplierFlagsForOrder(lineId, id),
      canDeleteOrder: slice.delete,
    };
    if (vatLine) {
      handleOrderVatEditor(editorPayload);
      return;
    }
    handleOrderEditor(editorPayload);
  };
  const ensureEditorSourceConfig = async () => {
    let cfg = general.getLocalStorage("user_config") || {};
    if (cfg?.normalLineNames) {
      setConfigData(cfg);
      return cfg;
    }
    try {
      const res = await axiosInstance.get("/api/users/getConfig");
      if (res?.data?.data) {
        general.setLocalStorage("user_config", res.data.data);
        cfg = res.data.data;
        setConfigData(cfg);
      }
    } catch (error) {
      console.error(error);
    }
    return cfg;
  };

  const handleOrderEditor = async (obj) => {
    const slice = orderPermSliceForLine(orderScreenPerm, obj?.lineId);
    if (!slice.create && !obj?.id) {
      toast.warning("Bạn không có quyền thêm mới.");
      return;
    }
    if (obj?.id && !slice.edit && !slice.view) {
      toast.warning("Bạn không có quyền xem chi tiết.");
      return;
    }
    if (!obj) obj = {};
    if (!obj?.id) await ensureEditorSourceConfig();
    setOrderEditorData({
      ...obj,
      ...muaHoSupplierFlagsForOrder(obj?.lineId, obj?.id, { requireMuaHoSupplier: obj?.requireMuaHoSupplier }),
      canDeleteOrder: slice.delete,
    });
  };
  const handleMultiOrderEditor = async (obj) => {
    if (!orderScreenPerm.normal.create) {
      toast.warning("Bạn không có quyền thêm mới.");
      return;
    }
    if (!obj) obj = {};
    await ensureEditorSourceConfig();
    setMultiOrderEditorData({
      ...obj,
      ...muaHoSupplierFlagsForOrder(obj?.lineId, obj?.id, { requireMuaHoSupplier: obj?.requireMuaHoSupplier }),
      canDeleteOrder: orderScreenPerm.normal.delete,
    });
  };
  const handleTransferCustomerEditor = (obj) => {
    if (!notifyPerm.loaded) {
      toast.info("Đang kiểm tra quyền...");
      return;
    }
    if (!notifyPerm.transferRequestInbox) {
      toast.warning("Bạn không có quyền nhận yêu cầu chuyển đơn.");
      return;
    }

    var listIds = selectedOrders;
    if (listIds.length === 0) {
      toast.error("Phải chọn ít nhất 1 mục");
      return;
    }
    if (!canTransferSelectedCustomers) {
      toast.error("Không thể đổi mã khách hàng cho danh sách đơn đã chọn.");
      return;
    }
    if (!obj) obj = {};
    obj.listIds = listIds;
    setTransterCustomerData(obj);
  };
  const handleTransferSackCode = (obj) => {
    if (!orderQuickUpdatePerm(ORDER_QUICK_UPDATE_SACK_CODE_FEATURE)) {
      toast.warning("Bạn không có quyền cập nhật mã bao.");
      return;
    }

    var listIds = selectedOrders;
    var orderDetailIds = selectedOrderDetails;
    if (listIds.length === 0 && orderDetailIds.length === 0) {
      toast.error("Phải chọn ít nhất 1 mục");
      return;
    }
    if (!obj) obj = {};
    obj.listIds = listIds;
    obj.detailIds = orderDetailIds;
    
    setTransterSackCodeData(obj);
  };
  const handleRecalculateFeeEditor = (obj) => {
    if (!orderQuickUpdatePerm(ORDER_QUICK_UPDATE_RECALCULATE_FEE_FEATURE)) {
      toast.warning("Bạn không có quyền tính toán lại chi phí.");
      return;
    }
    var listIds = selectedOrders;
    if (listIds.length === 0) {
      toast.error("Phải chọn ít nhất 1 mục");
      return;
    }
    if (!obj) obj = {};
    obj.listIds = listIds;
    setRecalculateFeeData(obj);
  };
  const handleOrderVatEditor = (obj) => {
    const lineId = obj?.lineId ?? ORDER_VAT_LINE_ID;
    const slice = orderPermSliceForLine(orderScreenPerm, lineId);
    if (!slice.create && !obj?.id) {
      toast.warning("Bạn không có quyền thêm mới.");
      return;
    }
    if (obj?.id && !slice.edit && !slice.view) {
      toast.warning("Bạn không có quyền xem chi tiết.");
      return;
    }
    if (!obj) obj = {};
    setVatEditorData({
      ...obj,
      ...muaHoSupplierFlagsForOrder(lineId, obj?.id, { requireMuaHoSupplier: obj?.requireMuaHoSupplier }),
      canDeleteOrder: slice.delete,
    });
  };
  const activeCurrentRow = (e) => {
    var targetTag = e.target;
    var tableTag = targetTag.closest('table');
    tableTag.querySelectorAll('tr').forEach(function (row) {
      row.classList.remove('active');
    });
    var trTag = targetTag.closest('tr');
    trTag.classList.add('active');
  };
  const handleViewHistoryInfo = (orderId, lineId, e) => {
    const slice = orderPermSliceForLine(orderScreenPerm, lineId);
    if (!slice.view && !slice.edit) {
      toast.warning("Bạn không có quyền xem lịch sử trạng thái đơn này.");
      return;
    }
    activeCurrentRow(e);

    var obj = {
      id: orderId,
      canEdit: slice.edit,
    };
    setHistoryInfoData(obj);
  };
  const handleViewOrderDetailHistoryInfo = (detailId, parentLineId, e) => {
    const slice = orderPermSliceForLine(orderScreenPerm, parentLineId);
    if (!slice.view && !slice.edit) {
      toast.warning("Bạn không có quyền xem lịch sử trạng thái đơn này.");
      return;
    }
    activeCurrentRow(e);

    var obj = {
      orderDetailId: detailId,
      canEdit: slice.edit,
    };
    setHistoryInfoData(obj);
  };

  const handleOpenCommentModal = (orderId) => {
    setCommentModalData({ orderId });
  };

  const handleCloseCommentModal = () => {
    setCommentModalData(null);
  };

  const handleExportPricing = async (obj) => {
    if (!orderExcelPerm(ORDER_EXCEL_EXPORT_FEATURES[0])) {
      toast.warning("Bạn không có quyền xuất mục này.");
      return;
    }
    downloadFileExcel('/api/import-orders/exportPricing', 'Bao-gia-mua-hang.xlsx');
  };

  const handleExportVatPricing = async (obj) => {
    if (!orderExcelPerm(ORDER_EXCEL_EXPORT_FEATURES[1])) {
      toast.warning("Bạn không có quyền xuất mục này.");
      return;
    }
    downloadFileExcel('/api/import-orders/exportVatPricing', 'Bao-gia-mua-hang-vat.xlsx');
  };

  const handleExportFile = async (obj) => {
    if (!orderExcelPerm(ORDER_EXCEL_EXPORT_FEATURES[3])) {
      toast.warning("Bạn không có quyền xuất mục này.");
      return;
    }
    downloadFileExcel('/api/import-orders/exportProfileVat', 'Ho-so-line-vat.xlsx');
  };

  //transportation
  const handleExportProfileNormal = async (obj) => {
    if (!orderExcelPerm(ORDER_EXCEL_EXPORT_FEATURES[2])) {
      toast.warning("Bạn không có quyền xuất mục này.");
      return;
    }
    downloadFileExcel('/api/import-orders/downloadProfileLineNormal', 'Ho-so-line-thuong.xlsx');
  };

  const handleInvoicePrivew = async (obj) => {
    if (!orderExcelPerm(ORDER_EXCEL_EXPORT_FEATURES[4])) {
      toast.warning("Bạn không có quyền xuất mục này.");
      return;
    }
    downloadFileExcel('/api/invoices/exportDraftVatOrders', 'Doi-soat-line-VAT-nhap.xlsx');
  };

  const downloadFileExcel = async (uri, fileName) => {
    var listIds = selectedOrders;
    if (listIds.length === 0) {
      toast.error("Phải chọn ít nhất 1 mục");
      return;
    }
    const textIds = listIds.join(';');
    var requestApi = `${API_URL_All}${uri}?codes=${textIds}`;

    setIsLoading2(true);
    general.downloadFile(requestApi, fileName);
    setIsLoading2(false);
  };


  async function saveDataRow(rowId, e) {
    var name = e.target.getAttribute('data-post-name');
    var value = e.target.getAttribute('data-post-value');
    console.log('Save row: ' + name + ': ' + value + ' id: ' + rowId);
    var dataModel = { id: rowId, name: name, code: value };
    try {
      var requestApi = `/api/import-orders/saveExpressOrder`;
      const response = await axiosInstance.put(requestApi, dataModel);
      if (!response.data.success) {
        console.log(response.data);
        toast.error(response.data.message);
        return;
      }
      if (response.data.data.colorCode) {
        var rowTag = document.querySelector(`tr[data-row-id='${rowId}']`);
        var progressTag = rowTag.querySelector(`td[data-name='${name}']`);
        progressTag.style.setProperty('--bs-table-bg-type', response.data.data.colorCode);
      }

      window.dispatchEvent(new Event('refresh-operation-info'));
      setTimeout(function () {
        setOperationObj(general.getLocalStorage("operation_info"));
      }, 2000);
    } catch (error) {
      toast.error(error.message);
    }

  }
  function removeItem(array, value) {
    return array.filter(item => item !== value);
  }
  const handleSelectOrder = (id) => {
    setSelectedOrders((prevSelected) => {
      //console.log('prevSelected: ' + prevSelected);
      const updated = prevSelected.includes(id)
        ? prevSelected.filter((orderId) => orderId !== id)
        : [...prevSelected, id];

      // Tự động select/unselect các detail orders
      const order = orders.find(o => o.id === id);
      if (order && order.details && order.details.length > 0) {
        const detailIds = order.details.map(detail => detail.id);
        const isOrderChecked = updated.includes(id);
        
        setSelectedOrderDetails(prevDetailSelected => {
          if (isOrderChecked) {
            // Add all detail IDs that are not already selected
            return [...new Set([...prevDetailSelected, ...detailIds])];
          } else {
            // Remove all detail IDs
            return prevDetailSelected.filter(detailId => !detailIds.includes(detailId));
          }
        });
      }

      var totalNumberPages = pageData.page.totalPages;
      var checkallTag = document.querySelector('.check_all');
      if (totalNumberPages === 1) {
        const allChecked = orders.every(order => updated.includes(order.id));
        if (checkallTag) checkallTag.checked = allChecked;
      }
      var isChecked = updated.includes(id);
      var uncheckedList = checkallTag.getAttribute('data-unchecked');
      var idText = id + '';
      if (totalNumberPages > 1 && (checkallTag.checked || uncheckedList)) {
        if (!uncheckedList) uncheckedList = '';
        var uncheckedItems = uncheckedList.split(',').filter(str => str !== "");
        if (isChecked) {
          uncheckedItems = removeItem(uncheckedItems, idText);
        } else {
          var isExisted = uncheckedItems.includes(idText);
          if (!isExisted) uncheckedItems.push(idText);
        }
        var textUncheckedItems = uncheckedItems.join(',');

        if (textUncheckedItems) {
          checkallTag.setAttribute('data-unchecked', textUncheckedItems);
          checkallTag.checked = false;
        } else {

          checkallTag.removeAttribute('data-unchecked');
          checkallTag.checked = true;
        }
      }

      return updated;
    }
    );
  };
  const handleSelectOrderDetail = (orderDetailId, orderId) => {
    setSelectedOrderDetails((prevSelected) => {
      const updated = prevSelected.includes(orderDetailId)
        ? prevSelected.filter((id) => id !== orderDetailId)
        : [...prevSelected, orderDetailId];
      return updated;
    });
  }
  const handleSelectAll = () => {
    var checkAllTag = document.querySelector('.check_all');
    checkAllTag.removeAttribute('data-unchecked');
    var isChecked = checkAllTag.checked;
    if (!isChecked) {
      // Nếu tất cả đơn hàng đã được chọn, bỏ chọn tất cả
      setSelectedOrders([]);
    } else {
      // Nếu chưa chọn hết, chọn tất cả đơn hàng
      setSelectedOrders(pageData.listIds);
    }
  };
  const openExpressFormData = (obj) => {
    const qk = quickUpdateFeatureKeyForFormType(obj?.formType);
    if (!qk || !orderQuickUpdatePerm(qk)) {
      toast.warning("Bạn không có quyền thực hiện mục này.");
      return;
    }
    if (selectedOrders.length > 0 || selectedOrderDetails.length > 0) {
      setExpressFormData(obj);
      setShowQuickUpdate(true);
    } else {
      toast.warning("Vui lòng chọn ít nhất một đơn hàng.", { position: "top-right" });
    }
  };
  const handleQuickUpdate = () => {
    if (!ORDER_QUICK_UPDATE_FEATURES.some((f) => orderQuickUpdatePerm(f))) {
      toast.warning("Bạn không có quyền Cập nhật nhanh.");
      return;
    }
    if (!orderScreenPerm.normal.edit && !orderScreenPerm.vat.edit) {
      toast.warning("Bạn không có quyền cập nhật.");
      return;
    }
    if (selectedOrders.length > 0 || selectedOrderDetails.length > 0) {
      setShowQuickUpdate(true);
    } else {
      toast.warning("Vui lòng chọn ít nhất một đơn hàng.", { position: "top-right" });
    }
  };
  const handleUpdateStatus = async () => {
    const qk = quickUpdateFeatureKeyForFormType(expressFormData.formType);
    if (!qk || !orderQuickUpdatePerm(qk)) {
      toast.warning("Bạn không có quyền thực hiện mục này.");
      return;
    }
    if (!orderScreenPerm.normal.edit && !orderScreenPerm.vat.edit) {
      toast.warning("Bạn không có quyền cập nhật.");
      return;
    }

    selectedIds = selectedOrders;
    selectedDetailIds = selectedOrderDetails;
    var checkAllTag = document.querySelector('.check_all');
    if (checkAllTag) {
      var uncheckedList = checkAllTag.getAttribute('data-unchecked');
      if (uncheckedList || checkAllTag.checked) {
        if (!uncheckedList) uncheckedList = '';
        var uncheckedItems = uncheckedList.split(',').map(item => parseInt(item.trim())).filter(Number.isInteger);
        var listIdsText = checkAllTag.getAttribute('data-listids');
        var listIds = listIdsText.split(',').map(item => parseInt(item.trim())).filter(Number.isInteger);
        selectedIds = listIds.filter(id => !uncheckedItems.includes(id));
      }
    }

    if (!expressFormData.formType) {
      toast.warning("Vui lòng chọn hình thức cập nhật !");
      return;
    }

    var dataModel = { ...expressFormData };
    dataModel.listIds = selectedIds;
    dataModel.detailIds = selectedDetailIds;
    if (dataModel.formType === 'progress' && !dataModel.progressId) dataModel.progressId = configData.progressList[0].id;
    if (dataModel.formType === 'checking' && !dataModel.checkingId) dataModel.checkingId = configData.checkingList[0].id;
    if (dataModel.formType === 'contacting' && !dataModel.contactingId) dataModel.contactingId = configData.checkingList[0].id;
    if (dataModel.formType === 'locking' && dataModel.locked === undefined) dataModel.locked = false;

    try {
      var requestApi = `/api/import-orders/changeOrderStatus`;
      const response = await axiosInstance.put(requestApi, dataModel);
      if (!response.data.success) {
        toast.error(response.data.message);
        return;
      }
      console.log(response);
      toast.success(response.data.message);

      setShowQuickUpdate(false);
      requestSearchOrders({ page: 0 });
      window.dispatchEvent(new Event('refresh-operation-info'));
      return;

    } catch (error) {
      toast.error(error.message);
      return;
    }


  };

  
  const handleAddOrderByExcel = () =>{
    if (!orderScreenPerm.normal.create) {
      toast.warning("Bạn không có quyền thêm mới.");
      return;
    }
    setAddOrderByExelModal(true);
  }

  const handleCloseAddOrderByExcelModal = () => {
    setAddOrderByExelModal(false);
  };
  const handleCloseTransferCustomerEditor = () => {
    setTransterCustomerData(null);
  };
  const selectedOrderRecords = orders.filter(order => selectedOrders.includes(order.id));
  const canTransferSelectedCustomers = selectedOrders.length > 0
    && selectedOrderRecords.length === selectedOrders.length;

  return (
    <div>
      <AdLayout>
        {orderFeaturePerm.loaded && !orderFeaturePerm.canView ? (
          <div className="container-fluid p-3 p-md-4">
            <div className="alert alert-warning mb-0">Bạn không có quyền truy cập màn hình này.</div>
          </div>
        ) : (
        <div className="table-responsive web-table order-admin-screen" data-loading={isLoading2}>
          <div className="container-fluid">
            <div className="row bg-dark">

              <div className="col-12 col-md-8 order-3 order-md-1">
                <div className="flex-start gap-2">
                  <button type="button" className="btn btn-success btn-sm d-none" onClick={handleOrderEditor} title="Thêm đơn hàng">
                    <FaPlus />
                  </button>

                  {orderScreenPerm.normal.create && (
                  <div className="dropdown">
                    <button className="btn btn-success btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                      <FaPlus />
                    </button>
                    <ul className="dropdown-menu">
                      <li onClick={handleOrderEditor}><a className="dropdown-item">Tạo đơn thường</a></li>
                      <li onClick={handleMultiOrderEditor}><a className="dropdown-item">Tạo nhiều đơn</a></li>
                      <li onClick={handleAddOrderByExcel}><a className="dropdown-item">Tạo nhiều đơn bằng file excel</a></li>
                    </ul>
                  </div>
                  )}

                  {orderScreenPerm.vat.create && (
                  <button type="button" className="btn btn-success btn-sm" onClick={handleOrderVatEditor} title="Thêm đơn VAT">
                    <FaPlus /> VAT
                  </button>
                  )}

                  {canCreateMuaHoAny && (
                  <div className="dropdown">
                    <button
                      className="btn btn-success btn-sm dropdown-toggle"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      title="Thêm đơn mua hộ (bắt buộc chọn nhà cung cấp)"
                    >
                      <FaPlus /> Mua hộ
                    </button>
                    <ul className="dropdown-menu">
                      {canCreateMuaHoNormal && (
                        <li onClick={() => handleOrderEditor({ requireMuaHoSupplier: true })}>
                          <a className="dropdown-item">Tạo đơn mua hộ thường</a>
                        </li>
                      )}
                      {canCreateMuaHoVat && (
                        <li onClick={() => handleOrderVatEditor({ requireMuaHoSupplier: true })}>
                          <a className="dropdown-item">Tạo đơn mua hộ VAT</a>
                        </li>
                      )}
                    </ul>
                  </div>
                  )}

                  <button type="button" className="btn btn-success btn-sm d-none" onClick={handleExportFile} title="Export file excel">
                    Xuất file
                  </button>
                  

                  {(orderScreenPerm.normal.create ||
                    orderScreenPerm.vat.create ||
                    canCreateMuaHoAny) && (
                  <div className="dropdown">
                    <button className="btn btn-success btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-file-earmark-plus-fill" viewBox="0 0 16 16">
                        <path d="M9.293 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.707A1 1 0 0 0 13.707 4L10 .293A1 1 0 0 0 9.293 0M9.5 3.5v-2l3 3h-2a1 1 0 0 1-1-1M8.5 7v1.5H10a.5.5 0 0 1 0 1H8.5V11a.5.5 0 0 1-1 0V9.5H6a.5.5 0 0 1 0-1h1.5V7a.5.5 0 0 1 1 0" />
                      </svg>
                    </button>
                    <ul className="dropdown-menu">
                      {orderScreenPerm.normal.create && (
                        <li onClick={() => handleOrderEditor({'isDraft': true})}><a className="dropdown-item">Tạo đơn nháp</a></li>
                      )}
                      {orderScreenPerm.vat.create && (
                        <li onClick={() => handleOrderVatEditor({ 'isDraft': true })}><a className="dropdown-item">Tạo đơn nháp VAT</a></li>
                      )}
                      {canCreateMuaHoNormal && (
                        <li onClick={() => handleOrderEditor({ isDraft: true, requireMuaHoSupplier: true })}>
                          <a className="dropdown-item">Tạo đơn nháp mua hộ thường</a>
                        </li>
                      )}
                      {canCreateMuaHoVat && (
                        <li onClick={() => handleOrderVatEditor({ isDraft: true, requireMuaHoSupplier: true })}>
                          <a className="dropdown-item">Tạo đơn nháp mua hộ VAT</a>
                        </li>
                      )}
                    </ul>
                  </div>
                  )}

                  {anyOrderExcelPerm && (
                  <div className="dropdown">
                    <button className="btn btn-success btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                      Excel
                    </button>
                    <ul className="dropdown-menu">
                      {orderExcelPerm(ORDER_EXCEL_EXPORT_FEATURES[0]) && (
                      <li onClick={() => handleExportPricing()}><a className="dropdown-item">Xuất BG MH thường</a></li>
                      )}
                      {orderExcelPerm(ORDER_EXCEL_EXPORT_FEATURES[1]) && (
                      <li className="" onClick={() => handleExportVatPricing()}><a className="dropdown-item">Xuất BG MH VAT</a></li>
                      )}
                      {orderExcelPerm(ORDER_EXCEL_EXPORT_FEATURES[2]) && (
                      <li onClick={() => handleExportProfileNormal()}><a className="dropdown-item">Xuất hồ sơ thường</a></li>
                      )}
                      {orderExcelPerm(ORDER_EXCEL_EXPORT_FEATURES[3]) && (
                      <li onClick={() => handleExportFile()}><a className="dropdown-item">Xuất hồ sơ VAT</a></li>
                      )}
                      {orderExcelPerm(ORDER_EXCEL_EXPORT_FEATURES[4]) && (
                      <li onClick={() => handleInvoicePrivew()}><a className="dropdown-item">Xuất đối soát VAT nháp</a></li>
                      )}
                      {orderExcelPerm(ORDER_EXCEL_EXPORT_FEATURES[5]) && (
                      <li onClick={() => downloadFileExcel('/api/import-orders/transportationPricing', 'Bao-gia-van-chuyen.xlsx')}><a className="dropdown-item">Báo giá vận chuyển thường</a></li>
                      )}
                      {orderExcelPerm(ORDER_EXCEL_EXPORT_FEATURES[6]) && (
                      <li onClick={() => downloadFileExcel('/api/import-orders/transportationPricingVat', 'Bao-gia-van-chuyen-vat.xlsx')}><a className="dropdown-item">Báo giá vận chuyển VAT</a></li>
                      )}
                    </ul>
                  </div>
                  )}
                  <button type="button" className="btn btn-primary btn-sm my-1 d-none" onClick={handleQuickUpdate} title="Cập nhật nhanh">
                    <FaSyncAlt />
                  </button>
                  {canUseOrderQuickUpdateToolbar && (
                  <div className="dropdown" disabled={selectedOrders.length < 1}>
                    <button className="btn btn-sm btn-primary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                      <FaSyncAlt />
                    </button>
                    <ul className="dropdown-menu" >
                      {orderQuickUpdatePerm(ORDER_QUICK_UPDATE_PROGRESS_FEATURE) && (
                      <li className="dropdown-submenu dropdown-2">
                        <a className="dropdown-item dropdown-toggle rotate-90">Lưu trình</a>
                        <ul className="dropdown-menu">
                          <li><a className="dropdown-item" onClick={() => openExpressFormData({formType:'progress', progressId: 3})}>Đơn mới</a></li>
                          <li><a className="dropdown-item" onClick={() => openExpressFormData({ formType: 'progress', progressId: 6 })} >Kho TQ</a></li>
                          <li><a className="dropdown-item" onClick={() => openExpressFormData({ formType: 'progress', progressId: 7 })}>Xe về VN</a></li>
                          <li><a className="dropdown-item" onClick={() => openExpressFormData({ formType: 'progress', progressId: 8 })}>Kho VN</a></li>
                          <li><a className="dropdown-item" onClick={() => openExpressFormData({ formType: 'progress', progressId: 9 })}>Đang giao</a></li>
                          <li><a className="dropdown-item" onClick={() => openExpressFormData({ formType: 'progress', progressId: 10 })}>Đã nhận</a></li>
                          
                        </ul>
                      </li>
                      )}
                      {orderQuickUpdatePerm(ORDER_QUICK_UPDATE_CHECKING_FEATURE) && (
                      <li className="dropdown-submenu dropdown-2">
                        <a className="dropdown-item dropdown-toggle rotate-90">Đối soát khách hàng</a>
                        <ul className="dropdown-menu">
                          <li><a className="dropdown-item" onClick={() => openExpressFormData({ formType: 'checking', checkingId: 13 })}>Chưa đối soát</a></li>
                          <li><a className="dropdown-item" onClick={() => openExpressFormData({ formType: 'checking', checkingId: 14 })}>Vào phiếu</a></li>
                          <li><a className="dropdown-item" onClick={() => openExpressFormData({ formType: 'checking', checkingId: 15 })}>Đã đối soát</a></li>
                        </ul>
                      </li>
                      )}
                      {orderQuickUpdatePerm(ORDER_QUICK_UPDATE_CONTACTING_FEATURE) && (
                      <li className="dropdown-submenu dropdown-2">
                        <a className="dropdown-item dropdown-toggle rotate-90">Đối soát đối tác</a>
                        <ul className="dropdown-menu">
                          <li><a className="dropdown-item" onClick={() => openExpressFormData({ formType: 'contacting', contactingId: 13 })}>Chưa đối soát</a></li>
                          <li><a className="dropdown-item" onClick={() => openExpressFormData({ formType: 'contacting', contactingId: 14 })}>Vào phiếu</a></li>
                          <li><a className="dropdown-item" onClick={() => openExpressFormData({ formType: 'contacting', contactingId: 15 })}>Đã đối soát</a></li>
                        </ul>
                      </li>
                      )}
                      {orderQuickUpdatePerm(ORDER_QUICK_UPDATE_LOCKING_FEATURE) && (
                      <li><a className="dropdown-item" onClick={() => openExpressFormData({ formType: 'locking' })}>Khóa đơn hàng</a></li>
                      )}
                      {canTransferSelectedCustomers && notifyPerm.loaded && notifyPerm.transferRequestInbox && (
                        <li><a className="dropdown-item" onClick={handleTransferCustomerEditor}>Đổi mã khách hàng</a></li>
                      )}
                      {orderQuickUpdatePerm(ORDER_QUICK_UPDATE_SACK_CODE_FEATURE) && (
                        <li><a className="dropdown-item" onClick={handleTransferSackCode}>Cập nhật mã bao</a></li>
                      )}
                      {orderQuickUpdatePerm(ORDER_QUICK_UPDATE_RECALCULATE_FEE_FEATURE) && (
                        <li><a className="dropdown-item" onClick={handleRecalculateFeeEditor}>Tính toán lại chi phí</a></li>
                      )}
                    </ul>
                  </div>
                  )}

                  <button type="button" className="btn btn-info btn-sm my-1" onClick={handleOpenModal}>
                    <FaFilter /> Bộ lọc
                  </button>

                  <div className="dropdown">
                    <button className={operationObj.overdueCount > 0 ? "btn btn-success btn-sm dropdown-toggle blinking-button22 warning-yellow" :"btn btn-success btn-sm dropdown-toggle"} type="button" data-bs-toggle="dropdown" aria-expanded="false">
                      <WarningIcon />
                    </button>
                    <ul className="dropdown-menu">
                      {operationObj.listProgress && operationObj.listProgress.map((item, index) => (
                        <li onClick={() => navigate(`/quan-li-don-hang?overdueId=${item.statusId}`)} key={index}>
                          <span className="dropdown-item">{item.name} - {item.count} đơn</span>
                        </li>
                      ))}
                      {operationObj.listProgress && operationObj.listProgress.length === 0 && (
                        <li>
                          <span className="dropdown-item">Không có đơn hàng nào chậm</span>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-7 col-md-2 py-1 fw-bold text-white text-uppercase d-flex align-items-center justify-content-star order-1 order-md-2">
                  Quản lý đơn hàng
                </div>

              <div className="col-5 col-md-2 py-1 order-2 order-md-3">
                <form id="form_filter">
                  <div className="input-group search-block">
                    <input name="keyword"
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Tìm kiếm..."
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e)}
                    />
                    <button onClick={(event) => requestSearchOrders({ page: 0 })} className="btn btn-light btn-sm" type="button" title="Tìm kiếm...">
                      <FaSearch />
                    </button>
                  </div>
                  </form>
                </div>
              </div>
            
            <div className="text-start mb-2 height-46">
              <div className="row">
                <div className="col-md-7 p-0 pt-1 express-filter">

                  <div className="d-inline" data-filter-ref="status">
                    {
                      countCountOrderByStatusList.map((obj, index) => (
                        <button key={index}
                          id={"btnFilter" + obj.statusId}
                          className={`btn btn-outline-secondary btn-sm me-1 my-1 ${obj.active ? 'active' : ''}`}
                          onClick={() => { handleExpressFilter('status',obj.name) }}
                        >
                          {obj.name} <span className="ant-tag">{obj.count}</span>
                        </button>
                      ))
                    }
                  </div>
                  {!params.overdue && ( 
                    <button 
                      className={`btn btn-outline-secondary btn-sm me-1 my-1 ${params.outdate ? 'active' : ''}`} 
                      onClick={() => { handleExpressFilter('outdate', 30) }}>
                        Quá hạn 30 ngày
                        <span className="ant-tag">{pageData.over30DaysCount}</span>
                    </button>
                  )}
                  {showMuaHoFilterButton && (
                  <button className={`btn btn-outline-secondary btn-sm me-1 my-1 ${params.support ? 'active' : ''}`} onClick={() => { handleExpressFilter('support', MUA_HO_ORDER_FILTER_SUPPORT_VALUE) }}>Mua hộ</button>
                  )}
                  {orderScreenPerm.loaded && isMuaHoOnlyOrderViewer(orderScreenPerm) && (
                  <span className="btn btn-outline-secondary btn-sm me-1 my-1 active disabled pe-none" title="Chỉ hiển thị đơn hàng mua hộ (có nhà cung cấp)">
                    Mua hộ
                  </span>
                  )}
                  <button className={`btn btn-outline-secondary btn-sm me-1 my-1 ${params.draft ? 'active' : ''}`} onClick={() => { handleExpressFilter('draft','Đơn nháp')}}>Đơn nháp</button>
                  
                </div>
                <div className="col-md-5 p-0 pt-1 ">
                  <span className="filter-des hidden-close-i2"></span>
                </div>
              </div>
            </div>
          </div>

          <section className="position-relative order-admin-screen__table">

            <TableColumnSettingsButton
              onClick={() => setShowColumnSettings(true)}
              disabled={!columnSettingsLoaded}
            />
            <TableColumnSettingsModal
              show={showColumnSettings}
              onClose={() => setShowColumnSettings(false)}
              screenKey={ORDER_MANAGEMENT_SCREEN_KEY}
              columns={orderToggleableColumns}
              allColumns={ORDER_MANAGEMENT_COLUMNS}
              hiddenColumns={hiddenCols}
              onSaved={(cols) => setHiddenCols(cols)}
            />
            <DataTable maxHeight="100%" className="table-normal" data-loading={isLoading}>
              <table className="table table-striped border-normal table-bordered">
                  <thead className="table-primary">
                    <tr>
                    <th rowSpan="2" className={`sticky ${getColCkeckedClass("select")}`} width="21px">
                        <input type="checkbox" title='Check all' className='check_all' onChange={() => handleSelectAll()} />
                      </th>
                    <th rowSpan="2" className={`sticky left-20 text-center ${getColCkeckedClass("orderId")}`}>
                        <span>Mã đơn</span>
                      </th>
                      <th rowSpan="2" className={`text-center ${getColCkeckedClass("createdDate")}`} style={{ minWidth: '100px' }}>
                        <span>Ngày</span>
                      <span className="filter-ib" data-form="form_filter" data-filter="dates" data-source-key="createdDate" data-source-url={API_URL_All + '/api/import-orders/searchDates'} data-default={year} data-min="100px"><i className="filter-i"></i></span>
                      </th>
                      <th rowSpan="2" className={getColCkeckedClass("lineName")} style={{ minWidth: '70px' }}>
                        <span>Line</span>
                        <span className="filter-ib" data-form="form_filter" data-filter="lines" data-source-key="list_lines" data-min="100px"><i className="filter-i"></i></span>
                    </th>
                    <th rowSpan="2" className={`text-center ${getColCkeckedClass("sackCode")}`} style={{ minWidth: '90px' }}>
                      <span>Mã bao</span>
                      <span className="filter-ib" data-form="form_filter" data-filter="sackCodes" data-source-key="sackCodes" data-source-url={API_URL_All + '/api/import-orders/searchSackCodes'} data-min="100px"><i className="filter-i"></i></span>
                    </th>
                    <th rowSpan="2" className={`text-center ${getColCkeckedClass("cnShippingCode")}`} style={{ minWidth: '165px' }}>
                      <span>Mã vận đơn TQ</span>
                      <span className="filter-ib" data-form="form_filter" data-filter="chinaCodes" data-source-key="cnShippingCode" data-min="120px" data-source-url={API_URL_All + '/api/import-orders/searchChinaCodes'}><i className="filter-i"></i></span>
                    </th>
                    <th rowSpan="2" className={`text-center ${getColCkeckedClass("vnShippingCode")}`} style={{ minWidth: '165px' }}>
                      <span>Mã vận VN</span>
                      <span className="filter-ib" data-form="form_filter" data-filter="vietnamCodes" data-source-key="vnShippingCode" data-min="120px" data-source-url={API_URL_All + '/api/import-orders/searchVietnamCodes'}><i className="filter-i"></i></span>
                    </th>
                    <th rowSpan="2" className={getColCkeckedClass("productName")} style={{ minWidth: '120px' }}>
                      <span>Tên đơn hàng</span>
                      <span className="filter-ib" data-form="form_filter" data-filter="productNames" data-source-url={API_URL_All + '/api/import-orders/searchProductNames'}><i className="filter-i"></i></span>
                    </th>
                    <th className={`text-center ${getColCkeckedClass("packageCount")}`} rowSpan="2" style={{ minWidth: '55px' }}>Kiện</th>
                    <th className={`text-center ${getColCkeckedClass("feeOfBase")}`} rowSpan="2" style={{ minWidth: '65px' }}>kg/m3</th>
                    <th className={`text-center ${getColCkeckedClass("feeOfBase")}`} rowSpan="2" style={{ minWidth: '55px' }}><span>Đ.Vị</span></th>
                    <th className={`text-center ${getColCkeckedClass("priceInsurance")}`} rowSpan="2" style={{ minWidth: '85px' }}><span data-note="vnđ">B.hiểm</span></th>
                    <th className={getColCkeckedClass("shippingMethod")} rowSpan="2" style={{ minWidth: '101px' }}>Phương thức lấy hàng</th>
                    <th className={getColCkeckedClass("note")} rowSpan="2" style={{ minWidth: '101px' }}>Ghi chú</th>
                    <th rowSpan="2" className={getColCkeckedClass("progress")} style={{ minWidth: '100px' }}>
                      <span>Lưu trình</span>
                      <span className="filter-ib" data-form="form_filter" data-filter="status" data-source-key="list_status" data-min="100px"><i className="filter-i"></i></span>
                    </th>
                    <th rowSpan="2" className={`text-center ${getColCkeckedClass("customerCode")}`} style={{ minWidth: '85px' }}>
                      <span>Mã KH</span>
                      <span className="filter-ib" data-form="form_filter" data-filter="customers" data-source-url={API_URL_All + '/api/users/searchUserCodes'} data-min="100px"><i className="filter-i"></i></span>
                    </th>
                    <th rowSpan="2" className={`text-center ${getColCkeckedClass("customerFeeOriginal")}`} style={{ minWidth: '90px' }} title="Phí khách chưa chiết khấu theo huy chương">
                        <span>ĐS KH gốc</span>
                    </th>
                    <th rowSpan="2" className={`text-center ${getColCkeckedClass("customerChecking")}`} style={{ minWidth: '100px' }}>
                        <span>Đối soát KH</span>
                        <span className="filter-ib" data-form="form_filter" data-filter="rateStatus" data-source-key="list_rateStatus" data-min="100px"><i className="filter-i"></i></span>
                    </th>

                    <th rowSpan="2" className={`text-center ${getColCkeckedClass("partnerCode")}`} style={{ minWidth: '85px' }}>
                      <span>Mã ĐT</span>
                      <span className="filter-ib" data-form="form_filter" data-filter="contacts" data-source-url={API_URL_All + '/api/contacts/searchContactCodes'} data-min="100px"><i className="filter-i"></i></span>
                    </th>
                    <th rowSpan="2" className={`text-center ${getColCkeckedClass("contactFee")}`} style={{ minWidth: '100px' }}>
                      <span>Đối soát ĐT</span>
                      <span className="filter-ib" data-form="form_filter" data-filter="contactRateStatus" data-source-key="list_rateStatus" data-min="100px"><i className="filter-i"></i></span>
                    </th>
                    <th rowSpan="2" className={`text-center ${getColCkeckedClass("profit")}`} style={{ minWidth: '90px' }}><span>Lợi nhuận</span></th>

                    <th rowSpan="2" className={`max-100 ${getColCkeckedClass("actions")}`}></th>
                    </tr>
                </thead>
                {pageData.page && pageData.page.content && pageData.page.content.map((order, index) => (
                  <tbody className="tbody-row" key={index}>
                    <tr key={order.id} data-row-id={order.id}>
                        <td className={`sticky ${getColCkeckedClass("select")}`} title={order.id}>
                          <input
                            type="checkbox"
                            checked={selectedOrders.includes(order.id)}
                            onChange={() => handleSelectOrder(order.id)}
                          />
                        </td>
                        <td className={`sticky left-20 text-center ${getColCkeckedClass("orderId")}`}>
                        <span
                          className="text-decoration-none"
                          style={{ color: "#485766", cursor: "pointer" }}
                          onClick={(e) => handleEditor(order.id, order.lineId, e)}
                          title={rowOrderPerm(order).edit ? "Cập nhật đơn hàng" : "Xem thông tin đơn hàng"}
                        >
                          {order.id}
                        </span>
                        </td>
                      <td className={`text-center ${getColCkeckedClass("createdDate")}`} title={`${order.orderDays} ngày`}>{general.formatTimeShort(order.createdDate)}</td>
                      <td className={`text-start ${getColCkeckedClass("lineName")}`}>{order.lineName}</td>
                      <td className={`${getColCkeckedClass("sackCode")} text-center`}>{order.sackCode}</td>
                      <td className={`text-danger text-center ${getColCkeckedClass("cnShippingCode")}`}>{order.cnShippingCode}</td>
                      <td className={`text-success text-center ${getColCkeckedClass("vnShippingCode")}`}>{order.vnShippingCode}</td>
                      <td className={`text-start text-truncate ${getColCkeckedClass("productName")}`} style={{ maxWidth: '150px', whiteSpace: 'wrap' }} title={order.name}>{order.name}</td>
                      <td className={`${getColCkeckedClass('packageCount')} text-center`}>{general.formatNumber(order.packageNumbers)}</td>
                      {!isOrderVatLine(order) && (<td className={`${getColCkeckedClass('feeOfBase')} text-center`}><span>{(order.freightCost >= order.freightCostM3 || order.freightCostM3 === null) ? general.formatDecimal(order.packageUnitValue) : general.formatDecimal(order.packageUnitM3)}</span></td>)}
                      {!isOrderVatLine(order) && (<td className={`${getColCkeckedClass('feeOfBase')} text-center`}><span>{(order.freightCost >= order.freightCostM3 || order.freightCostM3 === null) ? 'kg' : 'm3'}</span></td>)}
                      {isOrderVatLine(order) && (<td colSpan="2" className={`${getColCkeckedClass('feeOfBase')} text-center`}><span>{general.formatKgM3(order.packageUnitValue, order.packageUnitM3)}</span></td>)}
                      <td className={`${getColCkeckedClass('priceInsurance')} text-center`}>{general.formatNumber(order.insurancePrice)}</td>
                      <td className={`${getColCkeckedClass('shippingMethod')} text-start text-truncate mw-101`} title={order.shippingMethod}>{order.shippingMethod}</td>
                      <td className={`${getColCkeckedClass('note')} text-start text-truncate mw-101`} title={order.note}>
                          <div className="fw-bold text-danger">{general.mergeText(order.warningWeight, order.warningSize)}</div>
                          <span>{order.note}</span>
                      </td>
                      <td title={`${order.statusName} ${order.progressDays} ngày`} style={{ "--bs-table-bg-type": order.colorCode }} className={`text-start text_editor ${getColCkeckedClass("progress")}`} data-source-key="list_status" data-name="progressName">
                        {order.statusName}
                      </td>
                      <td className={`position-relative overflow-visible text-center ${getColCkeckedClass("customerCode")}`}>
                        <span>{order.customerCode}</span>
                      </td>
                      <td className={`${getColCkeckedClass('customerFeeOriginal')} text-center`}>
                        {general.formatNumber(order.feeOriginal ?? order.maxFreghtCost)}
                      </td>
                      <td className={`${getColCkeckedClass('customerChecking')} position-relative overflow-visible text-center`}>
                        <RankDiscountHover rank={order.userRank} rankDiscountPercent={order.rankDiscountPercent} feeOriginal={order.feeOriginal} fee={order.maxFreghtCost}>
                          <span className="pe-2">{general.formatNumber(order.maxFreghtCost)}</span>
                        </RankDiscountHover>
                        <CheckingTicket checkingId={order.statusRateId} checkingName={order.statusRateName} />
                       </td>
                        <td className="d-none">
                        </td>

                      <td className={`${getColCkeckedClass('partnerCode')} position-relative overflow-visible text-center`}>
                        <span>{order.contactCode}</span>
                      </td>
                      <td className={`text-center ${getColCkeckedClass('contactFee')}`} >
                        <span className="pe-1">{general.formatNumber(order.contactFee)}</span>
                        <CheckingTicket checkingId={order.contactingId} checkingName1={order.statusRateName} /></td>
                      <td className={`${getColCkeckedClass('profit')} text-center`}>{general.formatNumber(order.profit)}</td>

                      <td className={`max-100 ${getColCkeckedClass("actions")}`}>
                        <div className="d-flex justify-content-center1 gap-2">
                          <LockingTicket isLocking={order.locked} />
                          {(rowOrderPerm(order).view || rowOrderPerm(order).edit) && (
                          <span className="view_progress" title="Lịch sử trạng thái" onClick={(e) => handleViewHistoryInfo(order.id, order.lineId, e)}><EyeIcon /></span>
                          )}
                          {rowOrderPerm(order).edit && (
                          <a className="cursor-pointer" onClick={(e) => handleEditor(order.id, order.lineId, e)} title="Cập nhật đơn hàng">
                            <FaPen className="text-primary " size={15} />
                            </a>
                          )}
                          {rowOrderPerm(order).delete && !isDeleteActionHidden && (
                          <a onClick={(e) => handleDeleteOrder(order, e)} title="Xóa đơn hàng">
                              <FaTrash className="text-danger" size={15} />
                          </a>
                          )}
                          <button type="button" className="save_row d-none" onClick={(e) => saveDataRow(order.id, e)}>Save row</button>
                        </div>
                      </td>
                    </tr>
                    {isOrderVatLine(order) && order.details.length > 0 && order.details.map((detail, index) => (
                      <tr className={`row_sub${index % 2}`} data-detail-id={detail.id}>
                        {/* <td className='sticky'></td> */}
                        {/* add checkbox allow select the sub order */}
                        <td className={`sticky ${getColCkeckedClass("select")}`} title={order.id}>
                          <input
                            type="checkbox"
                            checked={selectedOrderDetails.includes(detail.id)}
                            onChange={() => handleSelectOrderDetail(detail.id)}
                          />
                          <span className='d-none'>{order.id}</span>
                        </td>
                        <td className={`sticky left-20 text-center ${getColCkeckedClass("orderId")}`}>{index + 1}</td>
                        <td className={getColCkeckedClass("createdDate")}><div className="ratio ratio-1x1"><img src={`${API_URL_All}/${detail.imagePath}`} /></div></td>
                        <td className={`text-start ${getColCkeckedClass("lineName")}`}>Mã SP: {detail.productCode}</td>
                        <td className={`${getColCkeckedClass("sackCode")} text-center`}>{detail.sackCode}</td>
                        <td className={`text-center max-120 ${getColCkeckedClass("cnShippingCode")}`}>
                          <div>
                            <span>{detail.cnCode}</span>
                          </div>
                        </td>
                        <td className={`text-center max-120 ${getColCkeckedClass("vnShippingCode")}`}>{detail.vnCode}</td>
                        <td className={`text-start text-truncate ${getColCkeckedClass("productName")}`} style={{ maxWidth: '150px', whiteSpace: 'wrap' }}>{detail.productName}</td>
                        <td className={`${getColCkeckedClass('packageCount')} text-center`}>{detail.packageCount}</td>
                        <td colSpan="2" className={`${getColCkeckedClass('feeOfBase')} text-start`}>
                          {detail.weight > 0 && (
                            <div>
                              Nặng: {general.formatDecimal(detail.weight)} kg
                            </div>
                          )}
                          {detail.size > 0 && (
                            <div>
                              Size: {general.formatDecimal(detail.size)} m3
                            </div>
                          )}
                        </td>
                        <td className={`${getColCkeckedClass('priceInsurance')} text-center`}>{general.formatNumber(detail.priceInsurance)}</td>
                        <td className={`${getColCkeckedClass('shippingMethod')} text-start text-truncate mw-101 small-blocks`}>
                          <div>
                            <span>S.Lượng: {detail.quantity}</span>
                          </div>
                          <div>
                            <span>Giá khai: {general.formatNumber(detail.priceDeclare)} vnđ</span>
                          </div>
                          <div>
                            <span>T.tiền: {general.formatNumber(detail.money)} vnđ</span>
                          </div>
                          {detail.shippingMethod}
                        </td>
                        <td className={`${getColCkeckedClass('note')} text-start text-truncate mw-101`}>{general.mergeText(detail.noteAdmin, detail.noteUser)}</td>

                        <td className={`text-start ${getColCkeckedClass("progress")}`} style={{ "--bs-table-bg-type": detail.progressColor }}>{detail.progressName}</td>
                        <td className={`text-start small-blocks ${getColCkeckedClass("customerCode")}`}>
                          <div>
                            <span>Thuế nhập: {detail.taxImport} %</span>
                          </div>
                          <div>
                            <span>Thuế VAT: {detail.taxVat} %</span>
                          </div>
                        </td>
                        <td className={`${getColCkeckedClass('customerFeeOriginal')} text-center`}>
                          {general.formatNumber(detail.feeOriginal ?? detail.fee)}
                        </td>
                        <td className={`${getColCkeckedClass('customerChecking')} position-relative overflow-visible text-center`} title={detail.description}>
                          <RankDiscountHover rank={order.userRank} rankDiscountPercent={order.rankDiscountPercent} feeOriginal={detail.feeOriginal} fee={detail.fee}>
                            <span className="pe-2">{general.formatNumber(detail.fee)}</span>
                          </RankDiscountHover>
                          <span><CheckingTicket checkingId={detail.checkingId} checkingName={detail.checkingName} /></span>
                        </td>
                        <td className="d-none">
                        </td>
                        <td className={`${getColCkeckedClass('partnerCode')} text-center`}><span>{detail.contactCode}</span></td>
                        <td className={`${getColCkeckedClass('contactFee')} text-center`}>
                          <span className="pe-2">{general.formatNumber(detail.contactFee)}</span>
                          <span><CheckingTicket checkingId={detail.contactingId} checkingName1={detail.checkingName} /></span>
                        </td>
                        <td className={`${getColCkeckedClass('profit')} text-center`}>{general.formatNumber(detail.profit)}</td>
                        <td className={`${getColCkeckedClass("actions")} text-start`}>
                          <div className="d-flex gap-2">
                            <LockingTicket isLocking={detail.locking} />
                            {(rowOrderPerm(order).view || rowOrderPerm(order).edit) && (
                            <span className="view_progress" title="Lịch sử trạng thái" onClick={(e) => handleViewOrderDetailHistoryInfo(detail.id, order.lineId, e)}><EyeIcon /></span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
                    ))}


                <tbody>
                    {errorMessage === '' && orders.length === 0 && !isLoading && (
                      <tr>
                        <td colSpan="24" className="text-center">Không tìm thấy dữ liệu</td>
                      </tr>
                    )}
                    {errorMessage !== '' && (
                      <tr>
                        <td colSpan="24" className="text-center">{errorMessage}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </DataTable>
            </section>
            <div className="order-admin-screen__footer d-flex justify-content-end align-items-center">
              <Paging
                pageData={pageData}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleChangeItemsPerPage}
                isShowTotal={true}
                showContactFeeTotal={!permissionHiddenCols.includes("contactFee")}
                showCheckingFeeTotal={!permissionHiddenCols.includes("customerChecking")}
              />
          </div>

            {/* Modal for Filter */}
            <Modal show={showQuickUpdate} onHide={() => setShowQuickUpdate(false)}>
              <Modal.Header closeButton>
                <Modal.Title>Cập nhật nhanh</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form style={{ maxWidth: "1200px", margin: "0 auto" }}>
                  <div style={{ display: "flex", gap: "20px" }}>
                    <Form.Group controlId="statusSelect" style={{ flex: 1 }}>
                      <Form.Label>Hình thức cập nhật</Form.Label>
                      <Form.Control
                        as="select"
                      value={expressFormData.formType}
                      onChange={(e) => setExpressFormData({ ...expressFormData, formType: e.target.value })}
                      >
                        <option value="">--Chọn--</option>
                        {orderQuickUpdatePerm(ORDER_QUICK_UPDATE_PROGRESS_FEATURE) && (
                          <option value="progress">Lưu trình</option>
                        )}
                        {orderQuickUpdatePerm(ORDER_QUICK_UPDATE_LOCKING_FEATURE) && (
                          <option value="locking">Khoá đơn hàng</option>
                        )}
                        {orderQuickUpdatePerm(ORDER_QUICK_UPDATE_CHECKING_FEATURE) && (
                          <option value="checking">Đối soát khách hàng</option>
                        )}
                        {orderQuickUpdatePerm(ORDER_QUICK_UPDATE_CONTACTING_FEATURE) && (
                          <option value="contacting">Đối soát đối tác</option>
                        )}
                      </Form.Control>
                    </Form.Group>
                    <Form.Group controlId="checkboxStatus" style={{ flex: 1, paddingTop: 7 }}>
                      <Form.Label></Form.Label>
                      <Form.Label></Form.Label>
                    {expressFormData.formType === "locking" && (
                        <div className=''>
                          <Form.Check
                            type="checkbox"
                            label="Khóa đơn hàng" // ghi rõ hơn ý nghĩa
                          checked={expressFormData.locked}
                          onChange={(e) => setExpressFormData({ ...expressFormData, locked: e.target.checked })}
                          />
                        </div>
                      )}
                    {expressFormData.formType === "progress" && (
                        <div className=''>
                          <Form.Control
                            as="select"
                          value={expressFormData.progressId}
                          onChange={(e) => setExpressFormData({ ...expressFormData, progressId: e.target.value })}
                        >
                          {configData.progressList.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name}
                              </option>
                            ))}
                          </Form.Control>
                        </div>
                      )}

                    {expressFormData.formType === "checking" && (
                        <div className=''>
                          <Form.Control
                            as="select"
                          value={expressFormData.checkingId}
                          onChange={(e) => setExpressFormData({ ...expressFormData, checkingId: e.target.value })}
                        >
                          {configData.checkingList.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name}
                              </option>
                            ))}
                          </Form.Control>
                        </div>
                      )}
                    {expressFormData.formType === "contacting" && (
                        <div className=''>
                          <Form.Control
                            as="select"
                          value={expressFormData.contactingId}
                          onChange={(e) => setExpressFormData({ ...expressFormData, contactingId: e.target.value })}
                        >
                          {configData.checkingList.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name}
                              </option>
                            ))}
                          </Form.Control>
                        </div>
                      )}
                    </Form.Group>
                  </div>
                </Form>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="primary" onClick={handleUpdateStatus}>
                  Cập Nhật
                </Button>
              </Modal.Footer>
            </Modal>

          {pageData.notFoundCodes && pageData.notFoundCodes.length > 0 && (
            <div className="mini-popup">
              <div className="mini-popup-header">
                <span>Mã đơn hàng thiếu</span>
                <i className="close-i close-i0" title="Close" onClick={() => setPageData({ ...pageData, notFoundCodes: [] })}></i>
              </div>
              <div>
                {pageData.notFoundCodes.map((item, index) => (
                  <div className="mini-popup-item" key={index}>{item}</div>
                ))}
              </div>
            </div>
          )}

          <input type="hidden" id="editor_lines" value={configData?.normalLineNames || ""} />
          <input type="hidden" id="list_lines" value={configData?.lineNames || ""} />
          <input type="hidden" id="list_status" value={configData?.progressNames || ""} />
          <input type="hidden" id="list_rateStatus" value={configData?.checkingNames || ""} />
          <input type="hidden" id="list_orderTypes" value="Đơn chuẩn,Bản nháp" />

          <div className="d-none th-col th filter-check display">
            <span className="filter-ib" data-form="form_filter" data-filter="deletion"><input type="checkbox" /></span>
            <label className="form-check-label">Đã xóa</label>
          </div>
          <div className="d-none th-col th filter-check display">
            <span className="filter-ib" data-form="form_filter" data-filter="draft"><input type="checkbox" /></span>
            <label className="form-check-label">Đơn nháp</label>
          </div>
          <div className="d-none th-col th display">
            <span className="filter-ib" data-form="form_filter" data-filter="outdate"></span>
            <label className="form-check-label">Quá hạn</label>
          </div>
          {showMuaHoFilterButton && (
          <div className="d-none th-col th display">
            <span className="filter-ib" data-form="form_filter" data-filter="support"></span>
            <label className="form-check-label">Mua hộ</label>
          </div>
          )}
          <div className="d-none th-col th display">
            <span className="filter-ib" data-form="form_filter" data-filter="dateRange"></span>
            <label className="form-check-label">Khoảng ngày</label>
          </div>
          <div className="d-none th-col th display">
            <span className="filter-ib" data-form="form_filter" data-filter="contactRateStatus"></span>
            <label className="form-check-label">Đối soát ĐT</label>
          </div>
          <div className="d-none th th-col display">
            <span className="filter-ib" data-form="form_filter" data-filter="overdue"><input type="checkbox" /></span>
            <label className="form-check-label">Đơn chậm</label>
          </div>

          {filterData && (
            <OrderAdminFilter data={filterData} handleSearch={requestSearchOrders} />
          )}
          {orderEditorData && (
            <OrderEditor data={orderEditorData} handleSearch={searchOrders} />
          )}
          {multiOrderEditorData && (
            <MultiOrderEditor data={multiOrderEditorData} handleSearch={searchOrders} />
          )}
          {vatEditorData && (
            <OrderVatEditor data={vatEditorData} handleSearch={searchOrders} />
          )}
          {historyInfoData && (
            <HistoryInfo data={historyInfoData} handleSearch={searchOrders}/>
          )}

          {transterCustomerData && (
            <TransferCustomerEditor
              data={{
                ...transterCustomerData,
                canSubmitTransfer: notifyPerm.loaded && notifyPerm.transferRequestProcess,
              }}
              handleClose={handleCloseTransferCustomerEditor}
              handleSearch={searchOrders}
            />
          )}

          {recalculateFeeData && (
            <RecalculateFeeEditor data={recalculateFeeData} handleSearch={searchOrders} />
          )}

          {transterSackCodeData && (
            <TransferSackCode data={transterSackCodeData} handleSearch={searchOrders} />
          )}

          {commentModalData && (
            <OrderCommentModal 
              orderId={commentModalData.orderId} 
              show={true} 
              handleClose={handleCloseCommentModal} 
            />
          )}

          <AddOrderByExcelModal
            show={addOrderByExelModal}
            handleClose={handleCloseAddOrderByExcelModal}
            handleSaved={requestSearchOrders}
          />
        </div >
        )}
      </AdLayout>

  </div>
  );
}
export default OrderAdmin;
